<template>
  <!-- ====================== -->
  <!-- TAB 3 : QUANTITY      -->
  <!-- ====================== -->
  <div>
    <div class="row mt-2 mb-2"></div>
    <p class="bb-hdg mt-2 mb-3">Quantity Details</p>
    <div class="row mt-2 mb-2">
      <template v-if="activeItem.tallyable">
        <div class="col-5">
          <CounterEmit
            ref="tallyQtyCounter"
            :pass_ref="'tally_qty'"
            :pass_label="'Tally Qty'"
            :pass_count="resolvedTallyQty"
            :pass_min="tallyQtyMin"
            :pass_max="99"
            :pass_digits="2"
            @change="onCounterChanged"
          />
        </div>
      </template>
      <div
        :class="activeItem.tallyable ? 'col-3 form-group' : 'col-3 form-group'"
      >
        <div
          v-if="isEncReadOnly"
          class="form-floating"
        >
          <input
            type="number"
            step="0.1"
            class="form-control text-center no-spinner"
            :value="encInputValue"
            id="enc"
            readonly
            disabled
            :class="{ 'is-invalid': errorFields.includes('enc') }"
          />
          <label
            class="bb-float-label optional"
            for="enc"
            >Enc Per Item</label
          >
        </div>
        <div
          v-else
          class="form-floating"
        >
          <input
            type="number"
            step="0.1"
            class="form-control text-center no-spinner"
            :value="encInputValue"
            id="enc"
            @input="onEncInput"
            @blur="onEncFieldBlur"
            @click="clearInputError('enc')"
            :class="{ 'is-invalid': errorFields.includes('enc') }"
          />
          <label
            class="bb-float-label"
            for="enc"
            >Enc Per Item</label
          >
        </div>
      </div>
      <template v-if="activeItem.tallyable">
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="formatEnc(calcQtyTotEnc)"
              id="qty_tot_enc"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="qty_tot_enc"
              >Total Enc</label
            >
          </div>
        </div>
      </template>
    </div>
    <div class="row mt-3 mb-2"></div>
    <div v-if="activeItem.countable">
      <p class="bb-sub-hdg mb-3 mt-2">Unit Details</p>
      <!-- -------------- -->
      <!--      ROW 2     -->
      <!-- -------------- -->
      <div class="row mt-2 mb-2">
        <!-- --------------------- -->
        <!--   unit name (input)   -->
        <!-- --------------------- -->
        <div class="col-5 form-group">
          <!-- read-only when static unit name defined for new campaign artefact -->
          <div
            v-if="isUnitNameReadOnly"
            class="form-floating"
          >
            <input
              readonly
              class="form-control text-center"
              :value="localItem.unit_name || activeItem.unit_name"
              id="unit_name"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="unit_name"
              >Unit Name</label
            >
          </div>
          <div
            v-else
            class="form-floating"
          >
            <v-select
              :model-value="localItem.unit_name ?? activeItem.unit_name"
              :options="units"
              class="form-control"
              placeholder="Select..."
              :class="{ 'is-invalid': errorFields.includes('unit_name') }"
              @keydown="onKeyDown($event)"
              @update:modelValue="onUnitSelect"
              id="unit_name"
              @click="clearInputError('unit_name')"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label"
              for="unit_name"
              >Unit Name</label
            >
          </div>
        </div>
      </div>
      <!-- -------------- -->
      <!--      ROW 3     -->
      <!-- -------------- -->
      <p class="bb-sub-hdg mt-3">Current {{ localItem.name }}</p>
      <div class="row mt-2 mb-2">
        <div
          v-if="isMaxUnitCountReadOnly"
          class="col-3 form-group"
        >
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="resolvedMaxUnitCount"
              id="max_unit_count"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="max_unit_count"
              >{{ maxUnitLabel }}</label
            >
          </div>
        </div>
        <div
          v-else
          class="col-5"
        >
          <CounterEmit
            ref="maxUnitCounter"
            :pass_ref="'max_unit_count'"
            :pass_label="maxUnitLabel"
            :pass_count="resolvedMaxUnitCount"
            :pass_min="1"
            :pass_max="100"
            :pass_digits="3"
            :invalid="errorFields.includes('max_unit_count')"
            @change="onCounterChanged"
          />
        </div>
        <div
          v-if="showCurUnitCount"
          class="col-5"
        >
          <CounterEmit
            ref="curUnitCounter"
            :pass_ref="'cur_unit_count'"
            :pass_label="curUnitLabel"
            :pass_count="resolvedCurUnitCount"
            :pass_min="0"
            :pass_max="resolvedMaxUnitCount"
            :pass_digits="2"
            @change="onCounterChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CounterEmit from './CounterEmit.vue'

export default {
  name: 'ItemQuantityTab',
  components: {
    CounterEmit,
  },
  props: {
    activeItem: {
      type: Object,
      required: true,
    },
    localItem: {
      type: Object,
      required: true,
    },
    units: {
      type: Array,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    formatEnc: {
      type: Function,
      required: true,
    },
    calcQtyTotEnc: {
      type: Number,
      required: true,
    },
    onCounterChanged: {
      type: Function,
      required: true,
    },
    clearInputError: {
      type: Function,
      required: true,
    },
    onEncInput: {
      type: Function,
      required: true,
    },
    onCountableToggle: {
      type: Function,
      required: true,
    },
    onKeyDown: {
      type: Function,
      required: true,
    },
    onUnitSelect: {
      type: Function,
      required: true,
    },
    isNewItem: {
      type: Boolean,
      required: true,
    },
    showCurUnitCount: {
      type: Boolean,
      default: false,
    },
    forceEncTwoDp: {
      type: Boolean,
      default: false,
    },
    onEncBlur: {
      type: Function,
      default: null,
    },
    tallyQtyMin: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    encInputValue() {
      const enc = this.activeItem?.enc

      if (!this.forceEncTwoDp) {
        return enc
      }

      if (enc === null || enc === undefined || enc === '') {
        return ''
      }

      return this.formatEnc(enc)
    },
    resolvedTallyQty() {
      const localValue = this.localItem?.tally_qty
      if (
        localValue !== null &&
        localValue !== undefined &&
        localValue !== ''
      ) {
        return Number(localValue)
      }

      const activeValue = this.activeItem?.tally_qty
      if (
        activeValue !== null &&
        activeValue !== undefined &&
        activeValue !== ''
      ) {
        return Number(activeValue)
      }

      return this.tallyQtyMin
    },
    unitLabelBase() {
      const unitName =
        this.localItem?.unit_name ?? this.activeItem?.unit_name ?? ''
      const trimmed = String(unitName).trim()
      return trimmed || 'Unit'
    },
    maxUnitLabel() {
      return `Max ${this.unitLabelBase}s`
    },
    curUnitLabel() {
      return `Cur ${this.unitLabelBase}s`
    },
    resolvedMaxUnitCount() {
      const localValue = Number(this.localItem?.max_unit_count)
      if (!Number.isNaN(localValue) && localValue > 0) {
        return localValue
      }

      const activeValue = Number(this.activeItem?.max_unit_count)
      if (!Number.isNaN(activeValue) && activeValue > 0) {
        return activeValue
      }

      return 1
    },
    resolvedCurUnitCount() {
      const localValue = Number(this.localItem?.cur_unit_count)
      if (!Number.isNaN(localValue) && localValue >= 0) {
        return localValue
      }

      const activeValue = Number(this.activeItem?.cur_unit_count)
      if (!Number.isNaN(activeValue) && activeValue >= 0) {
        return activeValue
      }

      return 0
    },
    campaignStaticItem() {
      const getStaticItemById =
        this.$store?.getters?.['characters/getStaticItemById']
      const getStaticItemByName =
        this.$store?.getters?.['characters/getStaticItemByName']

      const selectedStaticItemId =
        this.localItem?.item ?? this.activeItem?.item ?? null

      if (typeof getStaticItemById === 'function' && selectedStaticItemId) {
        const staticItem = getStaticItemById(selectedStaticItemId)
        if (staticItem) return staticItem
      }

      const selectedName = this.localItem?.name || this.activeItem?.name || ''
      if (typeof getStaticItemByName === 'function' && selectedName) {
        return getStaticItemByName(selectedName) ?? null
      }

      return null
    },
    isMaxUnitCountReadOnly() {
      const item = this.localItem || {}
      if (!item.campaign) return false
      // only readonly for new campaign artefacts
      if (this.isNewItem && item.type === 'Artefact') return true
      if (item._static_max_unit_count_readonly) return true
      return false
    },
    isEncReadOnly() {
      // ENC is always read-only - derived from the static Item definition
      return true
    },
    isCountableReadOnly() {
      const item = this.localItem || {}

      if (!item.campaign) return false

      const staticCountable = this.toBooleanFlag(
        this.campaignStaticItem?.countable,
      )

      if (this.campaignStaticItem && staticCountable === false) {
        return true
      }

      if (
        this.isNewItem &&
        item._static_countable_readonly &&
        this.toBooleanFlag(item.countable ?? this.activeItem?.countable) ===
          false
      ) {
        return true
      }

      return false
    },
    isUnitNameReadOnly() {
      const item = this.localItem || {}
      // if (!item.campaign) return false
      return !!item._static_unit_name_readonly
    },
  },
  methods: {
    onEncFieldBlur(event) {
      if (!event?.target) return

      const raw = event.target.value ?? ''
      const parsed = Number(raw)
      if (raw === '' || Number.isNaN(parsed)) return

      let emittedValue = parsed
      if (this.forceEncTwoDp) {
        const fixed = parsed.toFixed(2)
        event.target.value = fixed
        emittedValue = fixed
      }

      if (typeof this.onEncBlur === 'function') {
        this.onEncBlur(emittedValue)
      }
    },
    toBooleanFlag(value) {
      if (typeof value === 'string') {
        const normalised = value.trim().toLowerCase()
        if (normalised === 'true' || normalised === '1') return true
        if (normalised === 'false' || normalised === '0' || normalised === '') {
          return false
        }
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
  },
}
</script>
